// --- Our Modules --------------------------------------------------------------------------------
import useLang from "@/hooks/useLang";
import useTranslation from "@/hooks/useTranslation";
import type { TySupportStatus, TySupportTicket } from "@/types";
import { formatDateTime, pressToOpen, readLangValue } from "@/utils";
import Avatar from "@/UI/atoms/Avatar";
import Icon from "@/UI/atoms/Icon";
import StatusChip from "@/UI/atoms/StatusChip";
import SupportStepper from "@/UI/components/SupportStepper";
import "./styles.css";
// --- Logic --------------------------------------------------------------------------------------
type Props = {
    ticket: TySupportTicket;
    /** The whole tile opens the ticket — everything that has to be read in full is read there. */
    onOpen: () => void;
    /** Moves it along the trail, in place. */
    onStatus: (status: TySupportStatus) => void;
    /** Opens the ticket at its answer box — the same popup the tile itself opens. */
    onReply: () => void;
    isSaving?: boolean;
};

/**
 * One request for help, as a tile in the queue.
 *
 * **The two things an admin does all day are on the tile itself**: move it along, and answer it.
 * Opening a panel to press one of three dots is three clicks for a one-click act, and this queue is
 * worked by people who are not technical — the shorter the path from seeing the work to doing it,
 * the fewer of them stall halfway.
 *
 * The subject and two lines of the message are here so a queue can be triaged without opening six of
 * them. Everything longer than that — the whole message, the answer, the person's own card — is in
 * the popup, which is also the only way to that person's page: a second link down here would compete
 * with the one action the tile is for.
 *
 * **A ticket comes from a person, not from a company.** The name leads and the company sits under it,
 * both behind their picture, because the thing being answered is somebody's sentence and the company
 * is which account they wrote it from.
 */
export default function SupportTicketCard({ ticket, onOpen, onStatus, onReply, isSaving }: Props) {
    const t = useTranslation();
    const lang = useLang((state) => state.lang) ?? "ar";

    const company = ticket.organization ? readLangValue(ticket.organization.name, lang) || ticket.organization.slug : t.support.unknownCompany;
    const who = ticket.client?.name ?? t.support.unknownPerson;

    return (
        <article className="support-tile" role="button" tabIndex={0} onClick={onOpen} onKeyDown={pressToOpen(onOpen)}>
            <header className="support-tile__head">
                <Avatar name={who} picture={ticket.client?.picture ?? null} size={40} bordered />

                <div className="support-tile__who">
                    <span className="support-tile__person">{who}</span>
                    <span className="support-tile__company">{company}</span>
                </div>

                {/* Answered is a different fact from solved: we may have replied and still be waiting. */}
                {ticket.reply && <StatusChip size="sm" tone="accent" label={t.support.answered} />}
            </header>

            <div className="support-tile__body">
                <h2 className="support-tile__subject">{ticket.subject}</h2>
                <span className="support-tile__when">{formatDateTime(ticket.createdAt, lang)}</span>
                <p className="support-tile__message">{ticket.message}</p>
            </div>

            <SupportStepper status={ticket.status} onPick={onStatus} disabled={isSaving} />

            <div className="support-tile__actions">
                <button
                    type="button"
                    className="support-tile__reply"
                    onClick={(event) => {
                        event.stopPropagation();
                        onReply();
                    }}
                >
                    <Icon name="edit" size={16} />
                    <span>{ticket.reply ? t.support.editReply : t.support.respond}</span>
                </button>
            </div>
        </article>
    );
}
